import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { SimCard } from "../../models/SimCard";
import {
  fetchSimCards as fetchSimCardsApi,
  createSimCard as createSimCardApi,
  updateSimCard as updateSimCardApi,
  deleteSimCard as deleteSimCardApi,
} from "../../services/apiConfig";
import type { ListParams } from "../../services/api";

interface SimCardsState {
  simCards: SimCard[];
  /** The current page only - the server no longer sends the whole table. */
  total: number;
  /** How many cards sit behind each tab, across the whole list. */
  counts: { all: number; active: number; inactive: number; awaiting: number };
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

const initialState: SimCardsState = {
  simCards: [],
  total: 0,
  counts: { all: 0, active: 0, inactive: 0, awaiting: 0 },
  loading: "idle",
  error: null,
};

export const fetchSimCards = createAsyncThunk(
  "simCards/fetchSimCards",
  async (params?: ListParams) => {
    const response = await fetchSimCardsApi(params);
    return response;
  },
);

export const addSimCard = createAsyncThunk(
  "simCards/addSimCard",
  async (simCard: Omit<SimCard, "id" | "activation_time" | "last_update">) => {
    const response = await createSimCardApi(simCard);
    return response;
  },
);

export const editSimCard = createAsyncThunk(
  "simCards/editSimCard",
  async (simCard: SimCard) => {
    const response = await updateSimCardApi(simCard.id, simCard);
    return response;
  },
);

export const removeSimCard = createAsyncThunk(
  "simCards/removeSimCard",
  async (id: number) => {
    await deleteSimCardApi(id);
    return id;
  },
);

const simCardsSlice = createSlice({
  name: "simCards",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSimCards.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchSimCards.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.simCards = action.payload.data;
        state.total = action.payload.total;
        if (action.payload.counts) state.counts = action.payload.counts;
      })
      .addCase(fetchSimCards.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to fetch SIM cards";
      })
      .addCase(addSimCard.fulfilled, (state, action) => {
        state.simCards.push(action.payload);
      })
      .addCase(editSimCard.fulfilled, (state, action) => {
        const index = state.simCards.findIndex(
          (card) => card.id === action.payload.id,
        );
        if (index !== -1) {
          state.simCards[index] = action.payload;
        }
      })
      .addCase(removeSimCard.fulfilled, (state, action) => {
        state.simCards = state.simCards.filter(
          (card) => card.id !== action.payload,
        );
      });
  },
});

export default simCardsSlice.reducer;
